import { createSelector } from "@reduxjs/toolkit";
export type DetailsRow = { label: string; value: string };
const meaningful = (value?: string) => (value && value !== "—" ? value : undefined);

const selectRows = (rows: DetailsRow[]) => rows;
const selectGroups = createSelector([selectRows], (rows) => {
  const groups = new Map<string, DetailsRow[]>();
  let section = "Objekt";
  for (const row of rows) {
    if (/^— .+ —$/.test(row.label) && row.value === "") {
      section = row.label.slice(2, -2);
      continue;
    }
    let group = section === "Identifikasjon" ? "Om objektet" : section;
    let label = row.label;
    if (["MongoId", "GlobalId", "ExpressId"].includes(label) && section === "Identifikasjon")
      group = "Tekniske ID-er";
    if (
      ["Filnavn", "IFC-schema", "Siste versjon", "Sist endret av", "Opplastet"].includes(label) &&
      section === "Identifikasjon"
    )
      group = "Modell og kilde";
    if (section === "Egenskaper" && label.includes(".")) {
      const split = label.indexOf(".");
      group = label.slice(0, split);
      label = label.slice(split + 1);
    }
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push({ label, value: row.value });
  }
  // Keep model metadata and internal IDs after the useful object properties.
  const priority = (name: string) =>
    name === "Om objektet"
      ? -1
      : name === "Modell og kilde"
        ? 1
        : name === "Tekniske ID-er"
          ? 2
          : 0;
  return [...groups].sort(([a], [b]) => priority(a) - priority(b));
});
export const selectObjectDetailsView = createSelector(
  [
    selectRows,
    selectGroups,
    (_rows: DetailsRow[], search: string) => search.trim().toLocaleLowerCase(),
  ],
  (rows, groups, query) => {
    const value = (label: string) => meaningful(rows.find((row) => row.label === label)?.value);
    const title = value("Beskrivelse") ?? value("LongName") ?? value("Navn") ?? "Objektdetaljer";
    const visible = groups
      .map(([name, entries]) => ({
        name,
        open: Boolean(query) || (name !== "Tekniske ID-er" && name !== "Modell og kilde"),
        entries: entries
          .filter((row) => `${name} ${row.label} ${row.value}`.toLocaleLowerCase().includes(query))
          .map((row) => ({
            ...row,
            empty: !meaningful(row.value),
            displayValue: row.value || "—",
          })),
      }))
      .filter((group) => group.entries.length);
    return {
      title,
      subtitle: value("Navn") !== title ? value("Navn") : undefined,
      tags: [
        value("InstanceClass"),
        value("Type") ? `Type ${value("Type")}` : undefined,
        value("Etasje") ? `Etasje ${value("Etasje")}` : undefined,
      ].filter(Boolean),
      visible,
      query,
      count: visible.reduce((sum, group) => sum + group.entries.length, 0),
    };
  }
);
